import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '../api'; // axios instance (baseURL '/api', withCredentials: true)

type Me = {
  id: number;
  email: string;
  name?: string | null;
  role?: string | null;
  roles: string[];
  perms: string[];
  org_id?: number | null;
  status?: string | null;
};

type AuthContextValue = {
  user: Me | null;
  roles: string[];
  perms: string[];
  loading: boolean;                         // true only during initial /auth/me
  login: (email: string, password: string) => Promise<Me | null>;
  logout: () => Promise<void>;
  refresh: () => Promise<Me | null>;
};

const AuthCtx = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [perms, setPerms] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const setFromMe = (me: Me | null) => {
    setUser(me);
    setRoles(me?.roles ?? []);
    setPerms(me?.perms ?? []);
  };

  const fetchMe = async (): Promise<Me | null> => {
    try {
      const { data } = await api.get<Me>('/auth/me');
      setFromMe(data);
      return data;
    } catch (err: any) {
      // 401 before login is expected — treat as anonymous
      if (err?.response?.status === 401) {
        setFromMe(null);
        return null;
      }
      console.error('[Auth] /auth/me failed:', err);
      setFromMe(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMe();
  }, []);

  const login = async (email: string, password: string) => {
    await api.post('/auth/login', { email, password }); // server sets httpOnly cookie
    return fetchMe();
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setFromMe(null);
    }
  };

  const value = useMemo<AuthContextValue>(() => ({
    user, roles, perms, loading, login, logout, refresh: fetchMe,
  }), [user, roles, perms, loading]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
